import { useEffect, useState } from "react";

/** Pixels of travel in one direction before the bar reacts, so a finger's jitter does not make it flicker. */
const THRESHOLD = 8;
/** Near the top of the page the bar always shows. */
const TOP_ZONE = 64;

/**
 * Mobile bottom-tab behaviour (Instagram, X, Amazon): the bar slides away while the customer scrolls
 * down to read and comes back as soon as they scroll up, reach the top or reach the end of the page.
 * The window is the scroll container. `resetKey` (the pathname) brings the bar back on every navigation.
 */
export function useHideOnScroll(resetKey: string): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setHidden(false);
    let anchor = window.scrollY;
    let frame = 0;

    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      // iOS rubber-banding reports offsets outside [0, max]; clamping keeps the bounce from toggling the bar.
      const y = Math.min(Math.max(window.scrollY, 0), Math.max(max, 0));
      if (y <= TOP_ZONE || y >= max - 2) {
        setHidden(false);
        anchor = y;
        return;
      }
      const delta = y - anchor;
      if (Math.abs(delta) < THRESHOLD) return;
      setHidden(delta > 0);
      anchor = y;
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update); };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [resetKey]);

  return hidden;
}
